import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { BackOfficeService } from '../../../../services/backoffice.service';
import { takeUntil } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';
import { BackOffice24StatesService } from '../state/back-office24-states.service';
import { MyConfigOrganization } from '../../../../models/myConfig';
import { OrganizationGatesVM, OrganizationVehiclesVM, OrganizationVehicleEdit } from '../../../../models/orderModel';
import { OrgVehicleEditComponent } from './org-vehicle-edit/org-vehicle-edit.component';
import { MatDialog } from '@angular/material/dialog';
import { DialogConfirmComponent } from '../../../../../app/components/dialog-confirm/dialog-confirm.component';

@Component({
  selector: 'app-org-vehicles',
  standalone: false,
  templateUrl: './org-vehicles.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './org-vehicles.component.css'
})

export class OrgVehiclesComponent implements OnInit, OnDestroy {
  componentDestroyed$: Subject<boolean> = new Subject();
  orgSubscription: Subscription = new Subscription();
  organization: MyConfigOrganization;
  orgVehicles: OrganizationVehiclesVM[] = [];
  filteredVehicles: OrganizationVehiclesVM[] = [];
  searchQuery = '';
  isLoading = false;
  activeRequests = 0;
  gates: OrganizationGatesVM[] = [];
  selectedGateIds: string[] = [];

  constructor(
    private backOfficeService: BackOfficeService,
    private backOffice24StatesService: BackOffice24StatesService,
    private dialog: MatDialog
  ) { }

  setLoadingState(state: boolean) {
    this.isLoading = state;
  }

  incrementRequests() {
    this.activeRequests++;
    this.setLoadingState(true);
  }

  decrementRequests() {
    this.activeRequests--;
    if (this.activeRequests === 0) {
      this.setLoadingState(false);
    }
  }

  ngOnInit(): void {
    this.orgSubscription = this.backOffice24StatesService.currentOrganization$.subscribe(
      org => {
        this.organization = org;
        console.log(this.organization);
      }
    );

    this.getVehicleGates();
    this.getOrgGates();
  }

  getVehicleGates() {
    this.incrementRequests();
    this.backOfficeService.getOrganizationVehicleInfoByOrgId(this.organization.organizationId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(vehicles => {
        this.orgVehicles = vehicles || [];
        this.filteredVehicles = [...this.orgVehicles];
        this.decrementRequests();
      });
  }

  filterVehicles(): void {
    let filtered = [...this.orgVehicles];

    if (this.searchQuery.trim()) {
      const query = this.searchQuery.toLowerCase().trim();
      filtered = filtered.filter(vehicle =>
        vehicle.regNo.toLowerCase().includes(query) ||
        (vehicle.gates?.some(gateId => {
          const gate = this.getGateDetailsById(gateId);
          return gate?.name?.toLowerCase().includes(query);
        }))
      );
    }


    if (this.selectedGateIds.length > 0) {
      const hasNoGateOption = this.selectedGateIds.includes('no-gate');
      const gateIdsToFilter = this.selectedGateIds.filter(id => id !== 'no-gate');


      if (hasNoGateOption && gateIdsToFilter.length === 0) {
        filtered = filtered.filter(vehicle => !vehicle.gates || vehicle.gates.length === 0);
      }

      else if (!hasNoGateOption && gateIdsToFilter.length > 0) {
        filtered = filtered.filter(vehicle =>
          vehicle.gates?.some(gateId => gateIdsToFilter.includes(gateId))
        );
      }

      else if (hasNoGateOption && gateIdsToFilter.length > 0) {
        filtered = filtered.filter(vehicle =>
          !vehicle.gates ||
          vehicle.gates.length === 0 ||
          vehicle.gates.some(gateId => gateIdsToFilter.includes(gateId))
        );
      }
    }

    this.filteredVehicles = filtered;
  }

  toggleGateSelection(gateId: string): void {
    const index = this.selectedGateIds.indexOf(gateId);
    if (index === -1) {
      this.selectedGateIds.push(gateId);
    } else {
      this.selectedGateIds.splice(index, 1);
    }
    this.filterVehicles();
  }

  isGateSelected(gateId: string): boolean {
    return this.selectedGateIds.includes(gateId);
  }

  clearFilters(): void {
    this.searchQuery = '';
    this.selectedGateIds = [];
    this.filterVehicles();
  }

  getOrgGates(){
    this.incrementRequests();
    this.backOfficeService.getOrganizationGates(this.organization.organizationId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(gates => {
        this.gates = gates;
        this.decrementRequests();
      });
  }

  getGateDetailsById(id: string){
    return this.gates.find(gate => gate.id === id);
  }

  editVehicle(vehicle: OrganizationVehiclesVM) {
    const dialogRef = this.dialog.open(OrgVehicleEditComponent, {
      maxWidth: '95vw',
      maxHeight: '95vh',
      hasBackdrop: true,
      data: {
        vehicle: vehicle,
        gates: this.gates,
        organization: this.organization,
        edit: !!vehicle
      }
    });

    dialogRef.afterOpened().subscribe(() => {
      const componentInstance = dialogRef.componentInstance as OrgVehicleEditComponent;
      componentInstance.saveChangesEvent.subscribe((updatedVehicle: OrganizationVehiclesVM) => {
        if (updatedVehicle) {
          this.updateVehicle(updatedVehicle);
        }
      });
    });
  }

  updateVehicle(vehicle: OrganizationVehiclesVM) {
    const index = this.orgVehicles.findIndex(dc => dc.vehicleId === vehicle.vehicleId);
    if (index > -1) {
      const updatedVehicles = [...this.orgVehicles];
      updatedVehicles[index] = vehicle;
      this.orgVehicles = updatedVehicles;
    } else {
      this.orgVehicles = [vehicle, ...this.orgVehicles];
    }
    this.filterVehicles();
  }

  deleteVehicle(vehicle: OrganizationVehiclesVM) {
    const dialogRef = this.dialog.open(DialogConfirmComponent, {
      width: '450px',
      data: {
        title: 'FORM.CONFIRM_DELETE',
        message: 'VEHICLE_MANAGEMENT.DELETE_CONFIRMATION_MESSAGE',
        confirmText: 'FORM.DELETE',
        cancelText: 'FORM.CANCEL'
      }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.incrementRequests();
        this.backOfficeService.deleteVehicle(vehicle.vehicleId, this.organization.organizationId).subscribe({
          next: () => {
            this.loadVehicles();
          },
          error: (error) => {
            console.error('Error deleting vehicle:', error);
            this.decrementRequests();
          },
          complete: () => {
            this.decrementRequests();
          }
        });
      }
    });
  }

  loadVehicles() {
    this.incrementRequests();
    this.backOfficeService.getOrganizationVehicleInfoByOrgId(this.organization.organizationId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(vehicles => {
        this.orgVehicles = vehicles || [];
        this.filteredVehicles = [...this.orgVehicles];
        this.decrementRequests();
      });
  }

  ngOnDestroy() {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
    this.orgSubscription.unsubscribe();
  }
}
